import React from 'react';
import { useVariantSheet, UseVariantSheetProps } from './hooks/useVariantSheet';
import './styles/VariantSheetMobile.css';

const VariantSheetMobile: React.FC<UseVariantSheetProps> = (props) => {
  const {
    hasHierarchicalVariants,
    selectedVariant,
    setSelectedVariant,
    addLoading,
    selectedLevel1,
    selectedLevel2,
    selectedLevel3,
    selectedVariantObj,
    cartItem,
    cartQuantity,
    handleAddToCart,
    handleCartQuantityChange,
    getLevel1Options,
    getLevel2Options,
    getLevel3Options,
    handleLevel1Change,
    handleLevel2Change,
    handleLevel3Change,
    colorVariants,
    nonColorVariants,
  } = useVariantSheet(props);

  return (
    <div className="variant-sheet-mobile">
      {/* Hierarchical Variant UI */}
      {hasHierarchicalVariants && props.variantHierarchy && props.hierarchicalVariants ? (
        <div className="variant-hierarchical-section">
          {/* Level 1 */}
          <div className="variant-level">
            <h4>{props.variantHierarchy?.[0]}</h4>
            <div className="variant-options-row">
              {getLevel1Options().map((option) => {
                const hasAvailableVariants = props.hierarchicalVariants!.some(variant =>
                  variant.attributes[props.variantHierarchy?.[0] ?? ''] === option && variant.quantity > 0
                );
                return (
                  <button
                    key={option}
                    className={`variant-option-btn${selectedLevel1 === option ? ' selected' : ''}`}
                    onClick={() => hasAvailableVariants && handleLevel1Change(option)}
                    disabled={!hasAvailableVariants}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Level 2 */}
          {selectedLevel1 && props.variantHierarchy?.length && props.variantHierarchy.length >= 2 && getLevel2Options().length > 0 && (
            <div className="variant-level">
              <h4>{props.variantHierarchy?.[1]}</h4>
              <div className="variant-options-row">
                {getLevel2Options().map((option) => {
                  const hasAvailableVariants = props.hierarchicalVariants!.some(variant =>
                    variant.attributes[props.variantHierarchy?.[0] ?? ''] === selectedLevel1 &&
                    variant.attributes[props.variantHierarchy?.[1] ?? ''] === option &&
                    variant.quantity > 0
                  );
                  return (
                    <button
                      key={option}
                      className={`variant-option-btn${selectedLevel2 === option ? ' selected' : ''}`}
                      onClick={() => hasAvailableVariants && handleLevel2Change(option)}
                      disabled={!hasAvailableVariants}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {/* Level 3 */}
          {selectedLevel1 && selectedLevel2 && props.variantHierarchy?.length && props.variantHierarchy.length >= 3 && getLevel3Options().length > 0 && (
            <div className="variant-level">
              <h4>{props.variantHierarchy?.[2]}</h4>
              <div className="variant-options-row">
                {getLevel3Options().map((option) => {
                  const variant = props.hierarchicalVariants!.find(v =>
                    v.attributes[props.variantHierarchy?.[0] ?? ''] === selectedLevel1 &&
                    v.attributes[props.variantHierarchy?.[1] ?? ''] === selectedLevel2 &&
                    v.attributes[props.variantHierarchy?.[2] ?? ''] === option
                  );
                  const hasQuantity = variant && variant.quantity > 0;
                  return (
                    <button
                      key={option}
                      className={`variant-option-btn${selectedLevel3 === option ? ' selected' : ''}`}
                      onClick={() => hasQuantity && handleLevel3Change(option)}
                      disabled={!hasQuantity}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : (
        // Legacy variant UI
        <>
          {/* Color Variants */}
          {colorVariants.length > 0 && (
            <div className="variant-options-row">
              {colorVariants.map((variant, idx) => {
                let displayImage = (variant as any).variantImages?.[0] || (variant as any).variantImage;
                if (idx === 0) {
                  displayImage = displayImage || '';
                } else if (!displayImage || displayImage.trim() === '') {
                  displayImage = '/api/placeholder/80/80';
                }
                const hasQuantity = variant.quantity === undefined || variant.quantity > 0;
                return (
                  <div
                    key={variant.id}
                    className={`variant-option-btn variant-color${selectedVariant === variant.id ? ' selected' : ''}${!hasQuantity ? ' variant-option-disabled' : ''}`}
                    onClick={() => {
                      if (hasQuantity) {
                        setSelectedVariant(variant.id);
                        props.onVariantSelect?.(variant);
                      }
                    }}
                  >
                    <div className="variant-image-container">
                      <img src={displayImage} alt={variant.size} className="variant-image" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {/* Non-Color Variants */}
          {nonColorVariants.length > 0 && (
            <div className="variant-options-row">
              {nonColorVariants.map((variant) => {
                const hasQuantity = variant.quantity === undefined || variant.quantity > 0;
                return (
                  <div
                    key={variant.id}
                    className={`variant-option-btn variant-size-option${selectedVariant === variant.id ? ' selected' : ''}${!hasQuantity ? ' variant-option-disabled' : ''}`}
                    onClick={() => {
                      if (hasQuantity) {
                        setSelectedVariant(variant.id);
                        props.onVariantSelect?.(variant);
                      }
                    }}
                  >
                    <div className="size-variant-content">
                      <div className="size-variant-header">{variant.size}</div>
                      <div className="size-variant-body">
                        <div className="size-variant-price">₹ {Math.round(variant.price)}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
      {/* Price and Add to Cart */}
      {selectedVariantObj && (
        <div className="modal-price-add-mobile">
          <div className="mobile-price">₹ {Math.round(selectedVariantObj.price)}</div>
          {cartItem ? (
            <div className="cart-quantity-controls">
              <button
                className="cart-quantity-btn"
                onClick={() => handleCartQuantityChange(cartQuantity - 1)}
                disabled={cartQuantity <= 0}
              >
                -
              </button>
              <span className="cart-quantity-display">{cartQuantity}</span>
              <button
                className="cart-quantity-btn"
                onClick={() => handleCartQuantityChange(cartQuantity + 1)}
              >
                +
              </button>
            </div>
          ) : (
            <button onClick={handleAddToCart} disabled={addLoading} className="modal-add-to-cart-btn-mobile">
              {addLoading ? 'Adding...' : 'Add to Cart'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default VariantSheetMobile;
